import { useEffect, useRef } from 'react';
import { useHubspotEventMutation, useEngagementEvent } from '../../../hooks/api/useEvent';
import useAuth from '../../../hooks/utils/useAuth';
import { useRollbar } from '@rollbar/react';
import { ProviderName } from '../../../hooks/api/useExternalProviderPlans';
import { Resource } from './useResourceModalData';

export interface UseResourceModalAnalyticsProps {
  resource: Resource | undefined;
  isProviderResource: boolean;
  isGlobal: boolean;
  finalDetectedProviderName: ProviderName | undefined;
  errorMessage: string | undefined;
  urlError: boolean;
}

export const useResourceModalAnalytics = ({
  resource,
  isProviderResource,
  isGlobal,
  finalDetectedProviderName,
  errorMessage,
  urlError,
}: UseResourceModalAnalyticsProps) => {
  const { user } = useAuth();
  const hubspotMutation = useHubspotEventMutation();
  const postEngagementEvent = useEngagementEvent();
  const rollbar = useRollbar();
  const isFirstRun = useRef(true);
  const errorMessageHasFired = useRef<boolean>(false);

  // Track error messages
  if (isProviderResource && (errorMessage || urlError) && !errorMessageHasFired.current) {
    errorMessageHasFired.current = true;
    rollbar.error('Partner Access Error', { providerName: finalDetectedProviderName, errorMessage });
  }

  // Track resource view on mount (modal only mounts when open)
  useEffect(() => {
    if (isFirstRun.current && resource) {
      isFirstRun.current = false;
      if (isProviderResource && finalDetectedProviderName) {
        postEngagementEvent.mutate({
          engagementType: 'provider_view',
          metaData: {
            provider_name: finalDetectedProviderName,
            provider_plan_name: resource.name || '',
          },
        });
      } else {
        postEngagementEvent.mutate({
          engagementType: isGlobal ? 'global_resource_view' : 'client_resource_view',
          metaData: {
            resource_name: resource.name || '',
          },
        });
      }
    }
    return () => {
      isFirstRun.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resource?.id || resource?.name, isProviderResource, finalDetectedProviderName, isGlobal]);

  const handleHubspotCallButton = () => {
    if (isProviderResource && finalDetectedProviderName) {
      hubspotMutation.mutate({
        event: 'click_provider_call_button',
        properties: {
          email_address: user?.email_address!,
          package_code: user?.caller_role.active_subscription?.package.code!,
          provider_name: finalDetectedProviderName,
          provider_plan_name: resource?.name || '',
        },
      });
      postEngagementEvent.mutate({
        engagementType: 'provider_impression',
        metaData: {
          provider_name: finalDetectedProviderName,
          provider_plan_name: resource?.name || '',
          selected_website: false,
          selected_call: true,
        },
      });
    } else {
      hubspotMutation.mutate({
        event: isGlobal ? 'click_global_resource_impression' : 'click_client_resource_impression',
        properties: {
          email_address: user?.email_address!,
          package_code: user?.caller_role.active_subscription?.package.code!,
          resource_name: resource?.name || '',
          cta_action: 'phone_call',
        },
      });
      postEngagementEvent.mutate({
        engagementType: isGlobal ? 'global_resource_impression' : 'client_resource_impression',
        metaData: {
          resource_name: resource?.name || '',
          cta_action: 'phone_call',
        },
      });
    }
  };

  const handleHubspotLinkButton = () => {
    if (isProviderResource && finalDetectedProviderName) {
      hubspotMutation.mutate({
        event: 'click_provider_link',
        properties: {
          email_address: user?.email_address!,
          package_code: user?.caller_role.active_subscription?.package.code!,
          provider_name: finalDetectedProviderName,
          provider_plan_name: resource?.name || '',
        },
      });
      postEngagementEvent.mutate({
        engagementType: 'provider_impression',
        metaData: {
          provider_name: finalDetectedProviderName,
          provider_plan_name: resource?.name || '',
          selected_website: true,
          selected_call: false,
        },
      });
    } else {
      hubspotMutation.mutate({
        event: isGlobal ? 'click_global_resource_impression' : 'click_client_resource_impression',
        properties: {
          email_address: user?.email_address!,
          package_code: user?.caller_role.active_subscription?.package.code!,
          resource_name: resource?.name || '',
          cta_action: 'view_website',
        },
      });
      postEngagementEvent.mutate({
        engagementType: isGlobal ? 'global_resource_impression' : 'client_resource_impression',
        metaData: {
          resource_name: resource?.name || '',
          cta_action: 'view_website',
        },
      });
    }
  };

  return {
    handleHubspotCallButton,
    handleHubspotLinkButton,
  };
};
